/**
 * Background Sync API Wrapper
 *
 * Provides a type-safe wrapper around the Background Sync API for registering
 * and managing sync events that will fire when network connectivity is restored.
 */

export const SYNC_TAG = 'mutation-sync';

/**
 * Check if Background Sync API is supported
 */
export function isBackgroundSyncSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    return false;
  }

  return 'sync' in ServiceWorkerRegistration.prototype;
}

/**
 * Register a background sync event
 * This will trigger the service worker's 'sync' event when network is available
 */
export async function registerBackgroundSync(tag: string = SYNC_TAG): Promise<void> {
  if (!isBackgroundSyncSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // @ts-expect-error - sync is not in the TypeScript types yet
    await registration.sync.register(tag);
  } catch (error) {
    throw error;
  }
}

/**
 * Get list of registered sync tags
 */
export async function getRegisteredSyncTags(): Promise<string[]> {
  if (!isBackgroundSyncSupported()) {
    return [];
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // @ts-expect-error - sync is not in the TypeScript types yet
    const tags = await registration.sync.getTags();

    return tags;
  } catch {
    return [];
  }
}

/**
 * Cancel a sync event (if possible)
 * Note: Not all browsers support cancellation
 */
export async function cancelSync(tag: string = SYNC_TAG): Promise<void> {
  if (!isBackgroundSyncSupported()) {
    return;
  }

  // Background Sync API doesn't provide a way to cancel a registered sync
  // Once registered, it will fire when network is available
  // This is a no-op for now, but kept for API consistency
}

/**
 * Check if a specific sync tag is registered
 */
export async function isSyncRegistered(tag: string = SYNC_TAG): Promise<boolean> {
  const tags = await getRegisteredSyncTags();
  return tags.includes(tag);
}
